import { normalizeSlotText } from './slot-normalizer.util';

/** Levenshtein distance for short strings. */
export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const row = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) row[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = row[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = tmp;
    }
  }
  return row[n]!;
}

/** User tokens (length >= 2) for partial phrase matching. */
function userTokens(userNorm: string): string[] {
  return userNorm.split(/\s+/).filter(t => t.length >= 2);
}

function tokenMatchScore(userNorm: string, phraseNorm: string): number {
  if (!phraseNorm) return 999;
  if (userNorm === phraseNorm || userNorm.includes(phraseNorm) || phraseNorm.includes(userNorm)) {
    return 0;
  }
  const tokens = userTokens(userNorm);
  if (tokens.length === 0) return levenshtein(userNorm, phraseNorm);
  for (const tok of tokens) {
    if (phraseNorm.includes(tok)) return 0;
  }
  const joined = tokens.join(' ');
  if (joined.length >= 3 && (phraseNorm.includes(joined) || joined.includes(phraseNorm))) {
    return 0;
  }
  return levenshtein(userNorm, phraseNorm);
}

function minDistanceToPhrases(userNorm: string, phrases: string[]): number {
  let min = Infinity;
  for (const p of phrases) {
    if (!p) continue;
    const score = tokenMatchScore(userNorm, p);
    if (score < min) min = score;
  }
  return min === Infinity ? 999 : min;
}

/** Suggested max Levenshtein distance from longest label/alias length. */
export function maxDistanceForLabels(
  candidates: ReadonlyArray<{ label: string; aliases?: string[] }>,
  floor = 2,
  cap = 4
): number {
  let longest = 0;
  for (const c of candidates) {
    longest = Math.max(longest, normalizeSlotText(c.label).length);
    for (const a of c.aliases ?? []) {
      longest = Math.max(longest, normalizeSlotText(a).length);
    }
  }
  if (longest <= 12) return floor;
  if (longest <= 24) return Math.min(cap, 3);
  return cap;
}

/**
 * Best match among candidates (value + label + aliases).
 * Returns matched candidate value or null if no phrase within maxDistance.
 */
export function fuzzyPickOption(
  userText: string,
  candidates: ReadonlyArray<{ value: string; label: string; aliases?: string[] }>,
  maxDistance?: number
): string | null {
  const t = normalizeSlotText(userText);
  if (!t || !candidates.length) return null;

  const limit = maxDistance ?? maxDistanceForLabels(candidates);

  let bestValue: string | null = null;
  let bestScore = limit + 1;

  for (const c of candidates) {
    const phrases = [c.label, c.value, ...(c.aliases ?? [])].map(normalizeSlotText);
    const score = minDistanceToPhrases(t, phrases);
    if (score < bestScore) {
      bestScore = score;
      bestValue = c.value;
    }
  }

  return bestScore <= limit ? bestValue : null;
}
